import type { StudySessionStep } from '@formency/shared';
import type { AnsweredStep, StepAnswer, StepResult } from '@/features/session-player/types';

function reconstructStepAnswer(
  questionType: string | null,
  stepKind: string,
  responseJson: unknown
): StepAnswer {
  if (stepKind === 'info') return { type: 'info' };

  switch (questionType) {
    case 'mcq':
      return { type: 'mcq', selectedIds: Array.isArray(responseJson) ? responseJson : [] };
    case 'true_false':
      return { type: 'true_false', selected: responseJson === 'true' || responseJson === true };
    case 'text':
      return { type: 'text', value: typeof responseJson === 'string' ? responseJson : '' };
    case 'cloze':
      return {
        type: 'cloze',
        blanks:
          responseJson && typeof responseJson === 'object' && !Array.isArray(responseJson)
            ? (responseJson as Record<string, string>)
            : {},
      };
    case 'ranking':
      return { type: 'ranking', order: Array.isArray(responseJson) ? responseJson : [] };
    case 'classify':
      return {
        type: 'classify',
        assignments:
          responseJson && typeof responseJson === 'object' && !Array.isArray(responseJson)
            ? (responseJson as Record<string, string>)
            : {},
      };
    case 'cloze_diagram':
    case 'cloze_image':
      return {
        type: questionType as 'cloze_diagram' | 'cloze_image',
        blanks:
          typeof responseJson === 'object' && responseJson !== null
            ? (responseJson as Record<string, string>)
            : {},
      };
    case 'image_hotspot': {
      const response =
        typeof responseJson === 'object' && responseJson !== null
          ? (responseJson as { x?: number; y?: number })
          : {};

      return {
        type: 'image_hotspot',
        x: typeof response.x === 'number' ? response.x : null,
        y: typeof response.y === 'number' ? response.y : null,
      };
    }
    case 'numeric': {
      const response =
        typeof responseJson === 'object' && responseJson !== null
          ? (responseJson as { value?: number; unit?: string })
          : {};

      return {
        type: 'numeric',
        value: typeof response.value === 'number' ? response.value : null,
        ...(typeof response.unit === 'string' && { unit: response.unit }),
      };
    }
    default:
      return { type: 'info' };
  }
}

function extractFeedback(feedbackJson: unknown): string | null {
  if (typeof feedbackJson === 'string') return feedbackJson;

  if (feedbackJson && typeof feedbackJson === 'object' && 'feedback' in feedbackJson) {
    const feedback = (feedbackJson as Record<string, unknown>).feedback;
    return typeof feedback === 'string' ? feedback : null;
  }

  return null;
}

export interface ReconstructedStep extends AnsweredStep {
  responseTimeMs: number | null;
}

export function reconstructAnsweredSteps(
  steps: StudySessionStep[]
): Map<number, ReconstructedStep> {
  const map = new Map<number, ReconstructedStep>();

  steps.forEach((step, index) => {
    const responses = step.studySessionStepResponses;
    if (!responses?.length) return;

    const response = responses[responses.length - 1];
    const answer = reconstructStepAnswer(step.questionType, step.stepKind, response.responseJson);
    const explanation =
      (step.stepSnapshotPublicJson?.explanation as string | null | undefined) ?? null;
    const feedback = extractFeedback(response.feedbackJson);

    const result: StepResult = {
      isCorrect: response.isCorrect ?? false,
      score: response.score ?? 0,
      maxScore: response.maxScore,
      explanation,
      feedback,
      ...(response.correctChoiceIds && { correctChoiceIds: response.correctChoiceIds }),
      ...(response.blankResults && { blankResults: response.blankResults }),
      ...(response.rankingResults && { rankingResults: response.rankingResults }),
      ...(response.classifyResults && { classifyResults: response.classifyResults }),
      ...(response.hotspotResult && { hotspotResult: response.hotspotResult }),
      ...(response.numericResult && { numericResult: response.numericResult }),
    };

    map.set(index, {
      answer,
      result,
      response,
      responseTimeMs: response.responseTimeMs,
    });
  });

  return map;
}
